import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";
import defaultShareroom from "@assets/images/default_shareroom.png";
import { Path } from "@core/routing/path";
import { Group } from "@domain/group/group";
import { ImageVersions } from "@domain/media/dtos";
import { useHover } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { alpha, gap } from "@ui/styles/utils";
import { BackgroundImage } from "@ui/components/backgroundImage";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { Clamp } from "@ui/components/containers/clamp";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { CommunityIcon, ShareroomIcon, StarIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { MembersButton } from "@ui/components/profile/community/membersButton";
import { JoinGroupThumbnailButton } from "./joinGroupThumbnailButton";
import { DiscoverShareroomThumbnailButton } from "@ui/components/group/shareroom/discoverShareroomThumbnailButton";
import { ShareroomPricing } from "./shareroomPricing";
import { ShareroomThumbnailMenu } from "./shareroomThumbnailMenu";

interface ShareroomThumbnailSmallProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroom: Group;
	bannerHeight?: number;
	small?: boolean;
}
export const ShareroomThumbnailSmall: React.FC<ShareroomThumbnailSmallProps> = ({
	shareroom,
	bannerHeight,
	small,
	...htmlProps
}) => {
	const {
		banner,
		name,
		administrators: teachersList,
		categories,
		memberCounts,
		license,
		feedbacks: { meanScore },
	} = shareroom;
	const { isHovered, ref } = useHover();
	const memberStudentsCount = memberCounts.MEMBER ?? 0;

	return (
		<Container {...htmlProps} ref={ref}>
			<Thumbnail>
				<Link to={Path.Shareroom(shareroom)}>
					<StyledBackgroundImage
						image={banner}
						backdrop
						height={bannerHeight}
						defaultUrl={!banner ? defaultShareroom : undefined}
						size={ImageVersions.MEDIUM}
						isHovered={isHovered}
					/>
				</Link>
				<Infos>
					<Title size={2}>{name}</Title>

					{/* <Description size={1}>{description}</Description> */}

					{memberStudentsCount > 0 && (
						<Feature>
							<MembersCount small={small}>
								<CommunityIcon color={theme.colors.white} width={18} />
								<Members>{memberStudentsCount}</Members>
							</MembersCount>
							{meanScore ? (
								<Score>
									<StarIcon width={14} height={14} />
									<MeanScore>{meanScore.toFixed(1)}</MeanScore>
								</Score>
							) : null}
						</Feature>
					)}
					<TeachersLabel>
						<b>{I18n.t("group.SHAREROOM.thumbnail.animated_by")}</b>
					</TeachersLabel>
					<Line>
						<MembersButton sample={teachersList} count={3} spacing={-13} imageSize="default" />
						{license === "PREMIUM" ? <ShareroomPricing shareroom={shareroom} /> : null}
					</Line>
				</Infos>

				{categories[0] && categories[0] !== "OTHERS" && (
					<Category
						isTitleTruncated={true}
						renderIcons={() => <ShareroomIcon color={theme.colors.white} />}
						category={categories[0]}
						isSmall={true}
					/>
				)}

				<StyleMenu shareroom={shareroom} />
			</Thumbnail>
			{shareroom.license === "PREMIUM" ? (
				<StyleDiscoverButton shareroom={shareroom} />
			) : (
				<StyleJoinButton group={shareroom} />
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)`
	position: relative;
	display: flex;
	flex-direction: column;
	border-radius: 12px;
	cursor: pointer;
	overflow: hidden;
	background-color: ${({ theme }) => theme.colors.backgroundPrimary};

	@media (max-width: 900px) {
		padding-top: 0;
	}
	height: 310px;
	width: 250px;
`;

const Thumbnail = styled(RoundedBlock)`
	position: relative;
	display: flex;
	flex-direction: column;
	align-items: center;
	border-width: 0px;

	> a {
		width: 100%;
		height: 100%;
	}
`;

const StyledBackgroundImage = styled(BackgroundImage)<{ height?: number }>`
	height: ${({ height = 255 }) => height}px;
`;

const Infos = styled.div`
	${gap(3)};
	${({ theme }) => theme.textStyles.text};
	color: ${({ theme }) => theme.colors.white};
	padding: 15px;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-end;
	gap: 10px;
	position: absolute;
	top: 15px;
	left: 0;
	right: 0;
	bottom: 0;
	pointer-events: none;
`;

const StyleJoinButton = styled(JoinGroupThumbnailButton)`
	position: absolute;
	bottom: 13px;
	width: 80%;
	left: 6%;
`;
const StyleDiscoverButton = styled(DiscoverShareroomThumbnailButton)`
	position: absolute;
	bottom: 13px;
	width: 80%;
	left: 6%;
`;

const Title = styled(Clamp)<{ small?: boolean }>`
	${({ theme, small }) => (small ? theme.textStyles.text : theme.textStyles.subtitle)};
	color: ${theme.colors.white};
	position: absolute;
	top: 50px;
`;

// const Description = styled(Clamp)`
// 	font-size: 0.8125rem;
// 	font-weight: 500;
// `;

const TeachersLabel = styled.div`
	${({ theme }) => theme.textStyles.primary};
	color: ${theme.colors.white};
	font-size: O.5rem;
	font-weight: 700;
`;

const Category = styled(CategoryLabel)`
	position: absolute;
	left: 10px;
	top: 10px;
`;

const StyleMenu = styled(ShareroomThumbnailMenu)`
	position: absolute;
	right: 10px;
	top: 10px;
	background-color: ${({ theme }) => alpha(theme.colors.backgroundPrimary, 0.98)};
`;
const Line = styled.div`
	${row(0, "space-between", "center")}
	width: 100%;
`;
const MembersCount = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${row(0, undefined, "center")};
	${({ theme, small }) => (small ? theme.textStyles.small : theme.textStyles.secondary)};
	gap: 4px;
	width: 100%;
`;
const Members = styled.div`
	font-size: 12px;
	color: ${({ theme }) => theme.colors.white};
	font-weight: 200;
`;
const Feature = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${row(0, undefined, "center")};
	${({ theme, small }) => (small ? theme.textStyles.small : theme.textStyles.secondary)};
	gap: 15px;
`;
const Score = styled.div`
	${row(0, undefined, "center")};
	gap: 4px;
	@media (max-width: 900px) {
		align-self: flex-start;
	}
`;
const MeanScore = styled.div`
	color: ${({ theme }) => theme.colors.white};
	font-weight: 200;
	font-size: 12px;
`;

// const Review = styled.div`
//     ${row(0, undefined, "center")};
// 	gap: 4px;

// 	@media (max-width: 900px) {
// 		align-self: flex-start;
// 	}
// `;
// const FeedbackCount = styled.div`
//     color: ${({ theme }) => theme.colors.white};
//     font-weight: 200;
// 	font-size: 12px;
// `
